import FeatureLayer from '@arcgis/core/layers/FeatureLayer';
import Map from '@arcgis/core/Map';
import MapView from '@arcgis/core/views/MapView';
import WebMap from '@arcgis/core/WebMap';
import { useEffect, useState } from 'react';
import { loadPortalItem } from '../arcgis/jsapi/js-api-util';
import { getGeo } from '../nds/api';

// Report state.
const useReports = ({
    map,
    view,
    reportIds,
    layerIndex = 0,
    mastertable
}: {
    map: Map | WebMap | undefined;
    view: MapView | undefined;
    reportIds: string;
    layerIndex?: number;
    mastertable?: string;
}): any[] => {
    const [reports, setReports] = useState<any[]>([]);
    const [layerInfos, setLayerInfos] = useState<any>([]);
    const [activeLayerInfo, setActiveLayerInfo] = useState<any>();

    // reportIds changed.
    useEffect(() => {
        const onReportIdsChanged = async () => {
            if (
                view !== undefined &&
                map !== undefined &&
                reportIds !== undefined
            ) {
                /*
                Detect the report ID format - 2 formats

                see: https://esriuk.visualstudio.com/alt-instantatlas/_sprints/backlog/alt-instantatlas%20Team/alt-instantatlas/Sprints%202022/Q4%20Sprint%202?workitem=23278
                
                --------------------------------------------------------------------------------------------------

                A list of report IDs, layer IDs, id and name fields of the form:

                <report-id1>;<core-layer-id1>;<id-field1>;<name-field1>, 
                <report-id2>;<core-layer-id2>;<id-field2>;<name-field2>
                ,...,<report-idN>;<core-layer-idN>;<id-fieldN>;<name-fieldN>

                eg: 63aeddf1d7fc44b8b4dffcd868e84eac;G2;LTLACode;NAME, 
                0e80000fe3664109b42bc3010a02ab7c;G1;WardCode;NAME

                <report-idN> is the normal report GUID
                
                <core-layer-idN> is the ID of the layer within the master table - these are usually G<number> e.g. G1, G2, G3, G11 
                
                <id-fieldN> and <name-fieldN> are optional (in which case they must be assumed to be the 
                "normal" ones from the report) but are required if you use a mixed report-layer 
                option (i.e. you use a report designed for Wards for LSOAs).

                Report URL:

                https://reports.instantatlas.com/view-report/<report-id>/aggregate
                ?features=<features>
                &catalog=<mastertable>
                &layer=<core-layer-id>

                --------------------------------------------------------------------------------------------------

                As well as the standard list of report IDs 
                
                <report-id1>,<report-id2>,...,<report-idN>

                eg: "5ddc0e6852b84388995a1c2e0e57cca2,8badad3580cf4501af63e98f2d56ec9b,8587df19ef174f90be82baa8933ce7d6"
                
                Report URL:

                https://reports.instantatlas.com/view-report/<report-id>/aggregate?features=<features>
                */

                let items: any[] = [];

                // <report-id1>;<core-layer-id1>;<id-field1>;<name-field1>,
                // <report-id2>;<core-layer-id2>;<id-field2>;<name-field2>
                // ,...,<report-idN>;<core-layer-idN>;<id-fieldN>;<name-fieldN>
                if (reportIds.indexOf(';') !== -1 && mastertable) {
                    const arrTasks = reportIds
                        .split(',')
                        .map(async (reportInfo: string) => {
                            const [
                                reportId,
                                layerGeoId,
                                layerIdField,
                                layerNameField
                            ] = reportInfo.split(';');

                            const item = await loadPortalItem({ id: reportId });

                            // Get layer info.
                            const { Service_Url } = await getGeo({
                                url: mastertable,
                                id: layerGeoId
                            });

                            return {
                                id: item.id,
                                title: item.title,
                                layerUrl: Service_Url.toLowerCase(),
                                layerIdField,
                                layerNameField,
                                layerGeoId
                            };
                        });
                    items = await Promise.all(arrTasks);
                } else {
                    // <report-id1>,<report-id2>,...,<report-idN>
                    const arrTasks = reportIds
                        .split(',')
                        .map(async (id: string) => {
                            const item = await loadPortalItem({ id });
                            const data = await item.fetchData();

                            if (
                                data?.values?.instantatlas?.coreLayer !==
                                undefined
                            ) {
                                const [layerUrl, layerIdField, layerNameField] =
                                    data?.values?.instantatlas?.coreLayer.split(
                                        ';'
                                    );
                                return {
                                    id: item.id,
                                    title: item.title,
                                    layerUrl: layerUrl.toLowerCase(),
                                    layerIdField,
                                    layerNameField
                                };
                            } else if (
                                data?.values?.iaoReportDetails
                                    ?.numberOfParts !== undefined
                            ) {
                                // RB1.x - no core layer defined, need to fetch the whole design,
                                // ugly and probably slow but stops Pierre having to deal with all of it...
                                const nParts =
                                        data.values.iaoReportDetails
                                            .numberOfParts,
                                    designTxt = [];
                                for (let i = 1; i <= nParts; i++) {
                                    const partUrl = `${item.itemUrl}/info/iao-report-part-${i}.txt`,
                                        partJson = await fetch(partUrl).then(
                                            (rsp) => rsp.text()
                                        );
                                    designTxt.push(partJson);
                                }
                                const designJson = JSON.parse(
                                        designTxt.join('')
                                    ),
                                    primaryDataSource =
                                        designJson.dataSources.filter(
                                            (ds: any) =>
                                                ds.connectionType ===
                                                'ArcGISOnline'
                                        )[0];
                                if (primaryDataSource !== undefined) {
                                    const dsParams =
                                        primaryDataSource.parameters
                                            .split('&')
                                            .reduce(
                                                (map: any, item: string) => {
                                                    const [k, v] =
                                                        item.split('=');
                                                    return {
                                                        ...map,
                                                        [k]: decodeURIComponent(
                                                            v
                                                        )
                                                    };
                                                },
                                                {}
                                            );
                                    if (
                                        dsParams.src !== undefined &&
                                        dsParams.idField !== undefined &&
                                        dsParams.nameField !== undefined
                                    ) {
                                        return {
                                            id: item.id,
                                            title: item.title,
                                            layerUrl:
                                                dsParams.src.toLowerCase(),
                                            layerIdField: dsParams.idField,
                                            layerNameField: dsParams.nameField
                                        };
                                    }
                                }
                                return null;
                            }
                            return null;
                        });
                    items = await Promise.all(arrTasks);
                }

                const arrReports = items.filter((o: any) => o !== null);

                // Extract layers from reports.
                const arrLayerInfos = arrReports.reduce(
                    (acc: any[], report: any) => {
                        const layer: FeatureLayer = map.layers.find(
                            (lyr: any) =>
                                `${lyr.url}/${lyr.layerId}`.toLowerCase() ===
                                report.layerUrl
                        ) as FeatureLayer;

                        if (layer !== undefined) {
                            layer.outFields = ['*'];
                            const found = acc.find(
                                (lyr: any) => lyr.id === layer.id
                            );
                            return found === undefined
                                ? [
                                      ...acc,
                                      {
                                          layer,
                                          id: layer.id,
                                          title: layer.title,
                                          url: `${layer.url}/${layer.layerId}`.toLowerCase(),
                                          idField: report.layerIdField,
                                          geoId: report.layerGeoId,
                                          nameField: report.layerNameField
                                      }
                                  ]
                                : acc;
                        }
                        return acc;
                    },
                    []
                );

                setReports(arrReports);
                setLayerInfos(arrLayerInfos);
                setActiveLayerInfo(arrLayerInfos[layerIndex]);
            }
        };
        onReportIdsChanged();
    }, [layerIndex, map, mastertable, reportIds, view]);

    return [reports, layerInfos, activeLayerInfo, setActiveLayerInfo];
};
export default useReports;
